import React from 'react';
import * as R from 'ramda';
import {Table, Card, CardBody, Badge, Col} from 'reactstrap';
import {Maybe} from 'monet';

import {mapWithIndex, present, blank, numberFormat, maybe} from '@/util';
import Icon from '@/views/shared/Icon';
import styles from '@/b2b/index.module.css';
import AmoebaLink from '@/components/AmoebaLink';

const installmentList = mapWithIndex((i, idx) => (
  <Col key={`installment_${idx}`} style={{display: 'inline-block'}} md={4}>
    <Badge color={i.showWarning === true ? 'warning' : 'secondary'}>
      {i.timeline}
    </Badge>
    &nbsp;
    {i.dueDate}
  </Col>
));

const settlementList = mapWithIndex((s, idx) =>
  Maybe.fromFalsy(present(s))
    .map(() => (
      <Col key={`settlement_${idx}`} style={{display: 'inline-block'}} md={4}>
        <div className={styles.payout_item}>
          <div className={styles.payout_item_detail}>
            <Badge
              color={s.notFullySettled === true ? 'success' : 'danger'}
              style={{display: 'inline-block'}}
            >
              {s.timeline}
            </Badge>
            &nbsp;
            <div
              style={
                s.actual_and_not_fully_settled === true
                  ? {textDecoration: 'underline', display: 'inline-block'}
                  : {display: 'inline-block'}
              }
            >
              {numberFormat.toDollar(s.payAmount)}
            </div>
          </div>
          &nbsp;
          <div className={styles.payout_settlement_detail}>
            <div className={styles.payout_settlement_detail_heading}>
              <div className={styles.align_right}>
                <div className='badge badge-secondary'>Revenue Month</div>
                {s.revenueMonth}
              </div>
            </div>
            {maybe(R.length(s.settlements) !== 0).map(() => (
              <>
                <div className='badge badge-secondary'>Settlement Details</div>
                <div className={styles.payout_settlement_item_detail}>
                  <Badge
                    color={s.notFullySettled === true ? 'success' : 'danger'}
                    style={{display: 'inline-block'}}
                  >
                    {s.timeline}
                  </Badge>
                  <AmoebaLink href='/settlements/1628'>
                    {R.map(settlement => settlement.ref_no)(s.settlements)}
                  </AmoebaLink>
                  <br />
                  <div className='badge badge-secondary'>Bank-in Date</div>
                  {R.map(settlement => settlement.bank_in_on)(s.settlements)}
                  <div className='badge badge-secondary'>
                    Settled Amount
                  </div>${' '}
                  {R.map(settlement => settlement.settled_amount)(
                    s.settlements
                  )}
                </div>
              </>
            ))}
          </div>
        </div>
      </Col>
    ))
    .orNull()
);

const payoutList = mapWithIndex((payout, idx) => (
  <tr key={`revenue_payout_${idx}`} className={payout.trColor}>
    <td>
      <Badge
        color={payout.numColor}
        style={{
          paddingRight: '9px',
          paddingLeft: '9px',
          borderRadius: '9px',
        }}
      >
        {payout.num}
      </Badge>
    </td>
    <td>
      {Maybe.fromFalsy(payout.showArrowIcon)
        .map(() => (
          <Icon icon='arrow-right' style={{margin: '0 10px 0 20px'}} />
        ))
        .orNull()}
      <AmoebaLink href={`/producers/${payout.producerId}`}>
        {payout.producerName}
      </AmoebaLink>
    </td>

    <td className={styles.align_right}>
      <div className={styles.payout_item}>
        <div className={styles.payout_item_detail}>
          <Badge color={payout.badgeColor}>
            {numberFormat.toPercentage(payout.totalRate, 4)}
          </Badge>
        </div>

        {maybe(payout.totalRate > 0).map(() => (
          <div className={styles.payout_settlement_detail}>
            <div className={styles.payout_settlement_detail_heading}>
              <div className='badge badge-info'>Rate Details</div>
            </div>
            <div className={styles.payout_settlement_item_detail}>
              <div className='badge badge-secondary'>Initial</div>
              {R.prop('rateInfo', payout.payoutRatePopup)}
              <div className='badge badge-secondary'>Override</div>
              {R.prop('overrideRateInfo', payout.payoutRatePopup)}
              <div className='badge badge-secondary'>Effective Date</div>
              {R.prop('payoutEffectiveDate', payout.payoutRatePopup)}
            </div>
          </div>
        ))}
      </div>
    </td>
    <td>{settlementList(payout.settlementInfo)}</td>
    <th className='text-right'>$</th>
    <td className='text-right'>
      {numberFormat.toDecimal(payout.totalPayAmount)}
    </td>
  </tr>
));

const Overall = data => {
  if (blank(data)) {
    return null;
  }

  const {result} = data;

  return (
    <>
      <Card>
        <CardBody>
          <Table>
            <thead>
              <tr>
                <th style={{width: '5%'}} />
                <th style={{width: '28%'}}>Payee</th>
                <th className='text-right' style={{width: '10%'}}>
                  Total Rate
                </th>
                <th style={{width: '46%'}}>
                  Installment
                  <br />
                  {installmentList(result.installmentInfoTitle)}
                </th>
                <th style={{width: '1%'}} />
                <th className='text-right' style={{width: '10%'}}>
                  Pay-out Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {payoutList(result.overallPayouts)}
              <tr className='table-success'>
                <th scope='row' />
                <th scope='row' className='text-right'>
                  Total
                </th>
                <th scope='row' className='text-right'>
                  {numberFormat.toPercentage(result.totalRate, 4)}
                </th>
                <th scope='row'>
                  <span className='badge badge-secondary'>
                    {' '}
                    <Icon icon='info' />
                  </span>
                  &nbsp; commission statement will <strong>only</strong> show{' '}
                  <u>underlined items</u>
                  &nbsp;
                  <p style={{float: 'right'}}>Total</p>
                </th>
                <th scope='row' className='text-right'>
                  $
                </th>
                <th scope='row' className='text-right'>
                  {numberFormat.toDecimal(result.payOutAmount)}
                </th>
              </tr>
            </tbody>
          </Table>
        </CardBody>
      </Card>
    </>
  );
};

export default Overall;
